import { useCallback, useEffect, useRef, useState } from 'react';

const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000').replace(/\/$/, '');

export interface DemoChatMessage {
  speaker: 'ai';
  text: string;
  timestamp: Date;
}

interface DemoConversationTurn {
  speaker: 'operator' | 'client';
  text: string;
}

export function useDemoAIChat(scenarioId: string, sessionToken: string | null) {
  const [messages, setMessages] = useState<DemoChatMessage[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    setMessages([]);
    setIsTyping(false);
    setAiError(null);
    abortRef.current?.abort();
    abortRef.current = null;
  }, [scenarioId]);

  const sendMessage = useCallback(async (operatorMessage: string, history: DemoConversationTurn[]) => {
    const text = operatorMessage.trim();
    if (!text || isTyping) return;

    setAiError(null);
    setIsTyping(true);

    try {
      const controller = new AbortController();
      abortRef.current = controller;

      const response = await fetch(`${API_BASE_URL}/api/demo-chat/message`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionToken ? { 'x-demo-session-token': sessionToken } : {}),
        },
        body: JSON.stringify({
          scenarioId,
          message: text,
          history,
          sessionToken,
        }),
        signal: controller.signal,
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        setAiError(payload?.error || `Erro ao contatar IA (${response.status}).`);
        setIsTyping(false);
        return;
      }

      const reader = response.body?.getReader();
      if (!reader) {
        setIsTyping(false);
        return;
      }

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop() ?? '';

        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith('data: ')) continue;
          try {
            const data = JSON.parse(line.slice(6));
            if (data.type === 'done' && data.fullResponse) {
              setMessages((prev) => [
                ...prev,
                {
                  speaker: 'ai',
                  text: data.fullResponse,
                  timestamp: new Date(),
                },
              ]);
              setIsTyping(false);
            }
            if (data.type === 'error' && data.error) {
              setAiError(data.error);
            }
          } catch {
            // ignore malformed SSE lines
          }
        }
      }
    } catch (err: any) {
      if (err?.name !== 'AbortError') {
        setAiError('Erro de conexao com o servico de IA.');
      }
    } finally {
      setIsTyping(false);
      abortRef.current = null;
    }
  }, [isTyping, scenarioId, sessionToken]);

  const cancelTyping = useCallback(() => {
    abortRef.current?.abort();
    setIsTyping(false);
  }, []);

  return {
    messages,
    sendMessage,
    isTyping,
    aiError,
    cancelTyping,
  };
}
